import { PDFDocument, PDFName, degrees, type PDFPage, type PDFRef } from "pdf-lib";
import { raeumeAuf } from "./pdfAufraeumen";

/**
 * Seitenlogik des PDF-Editors: umsortieren, entfernen, drehen und Seiten aus
 * anderen Dokumenten einfügen (= zusammenführen). Reines pdf-lib, kein React —
 * der Seiten-Organisator hält nur die Liste `SeitenEintrag[]` und ruft beim
 * Übernehmen `baueDokument` auf.
 */

export type Drehung = 0 | 90 | 180 | 270;

/** Eine Seite in der Zielreihenfolge. `quelle` = Id einer SeitenQuelle, `seite` 0-basiert. */
export interface SeitenEintrag {
    key: string;
    quelle: string;
    seite: number;
    /** Zusätzliche Drehung im Uhrzeigersinn — kommt zur vorhandenen /Rotate hinzu. */
    drehung: Drehung;
}

/** Ein PDF, aus dem Seiten stammen. */
export interface SeitenQuelle {
    id: string;
    /** Dateiname — für das Etikett auf fremden Kacheln und Fehlermeldungen. */
    name: string;
    bytes: Uint8Array;
    seitenzahl: number;
}

/** `HAUPT` ist das bearbeitete Dokument selbst. */
export const HAUPT = "haupt";

/**
 * Fehler mit eigener, deutscher Meldung für den Nutzer. Alles andere (etwa
 * englische Meldungen von pdf-lib) zeigt die UI nur als allgemeinen Hinweis.
 */
export class SeitenFehler extends Error {
    constructor(meldung: string) {
        super(meldung);
        this.name = "SeitenFehler";
    }
}

export class GeschuetztesPdfFehler extends SeitenFehler {
    constructor(public readonly dateiname: string) {
        super(`„${dateiname}“ ist geschützt und lässt sich nicht einfügen.`);
        this.name = "GeschuetztesPdfFehler";
    }
}

// Laufender Zähler: dieselbe Quelle darf mehrfach eingefügt werden, die Keys
// der Kacheln müssen trotzdem eindeutig bleiben (dnd-kit, React).
let zaehler = 0;

export function seitenAusQuelle(q: SeitenQuelle): SeitenEintrag[] {
    return Array.from({ length: q.seitenzahl }, (_, seite) => ({
        key: `${q.id}:${seite}:${++zaehler}`,
        quelle: q.id,
        seite,
        drehung: 0 as Drehung,
    }));
}

export function verschiebe<T>(liste: T[], von: number, nach: number): T[] {
    const kopie = liste.slice();
    const [x] = kopie.splice(von, 1);
    kopie.splice(nach, 0, x);
    return kopie;
}

export const naechsteDrehung = (d: Drehung): Drehung => ((d + 90) % 360) as Drehung;

async function lade(q: SeitenQuelle): Promise<PDFDocument> {
    try {
        // Kopie: pdf-lib hält Referenzen auf den Puffer, PDF.js überträgt ihn ggf. an den Worker.
        return await PDFDocument.load(q.bytes.slice(), { updateMetadata: false });
    } catch (e) {
        if (istVerschluesselt(e)) throw new GeschuetztesPdfFehler(q.name);
        throw e;
    }
}

/**
 * pdf-lib wirft `EncryptedPDFError` — aber als ES5-Transpilat (tslib
 * `__extends` auf `Error`), da greift `instanceof` nicht. Erkannt wird darum
 * an der festen Meldung.
 */
export function istVerschluesselt(e: unknown): boolean {
    return e instanceof Error && /\bis encrypted\b/.test(e.message);
}

/**
 * Baut aus den Quellen ein PDF in der Reihenfolge von `eintraege`.
 *
 * Umgebaut wird IM Hauptdokument, nicht in einem frisch erzeugten
 * (`PDFDocument.create()`): so bleiben Formularfelder (AcroForm), Metadaten und
 * Lesezeichen des Hauptdokuments erhalten. Fremde Seiten kommen per `copyPages`
 * hinein. Hauptseiten, die nicht in `eintraege` stehen, fallen weg.
 */
export async function baueDokument(
    quellen: ReadonlyMap<string, SeitenQuelle>,
    eintraege: readonly SeitenEintrag[],
): Promise<Uint8Array> {
    if (eintraege.length === 0) throw new SeitenFehler("Mindestens eine Seite muss bleiben.");
    const haupt = quellen.get(HAUPT);
    if (!haupt) throw new SeitenFehler("Hauptdokument fehlt.");
    const ziel = await lade(haupt);
    const hauptSeiten = ziel.getPages();

    // Fremde Seiten je Quelle gesammelt kopieren — ein copyPages-Aufruf je Quelle
    // teilt gemeinsame Ressourcen (Schriften, Bilder) statt sie zu vervielfachen.
    // Wird dieselbe fremde Seite zweimal eingefügt, braucht jede Kachel ein
    // eigenes PDFPage-Objekt — daher je Vorkommen kopieren, nicht je Seite.
    const fremd = new Map<string, PDFPage[]>();
    for (const [id, q] of quellen) {
        if (id === HAUPT) continue;
        const benoetigt = eintraege.filter((e) => e.quelle === id).map((e) => e.seite);
        if (benoetigt.length === 0) continue;
        fremd.set(id, await ziel.copyPages(await lade(q), benoetigt));
    }

    const gesehen = new Set<number>();
    const reihenfolge = eintraege.map((e) => {
        if (e.quelle === HAUPT) {
            // Eine Hauptseite ist ein Objekt im Dokument — zweimal einsetzen
            // hieße dasselbe Seitenobjekt doppelt im Seitenbaum. Der Organisator
            // bietet kein Duplizieren an.
            if (gesehen.has(e.seite)) throw new SeitenFehler("Seite doppelt");
            const page = hauptSeiten[e.seite];
            if (!page) throw new SeitenFehler(`Seite ${e.seite + 1} fehlt im Hauptdokument.`);
            gesehen.add(e.seite);
            return { page, drehung: e.drehung };
        }
        // Kopien in der Reihenfolge des Filters oben abholen.
        const page = fremd.get(e.quelle)?.shift();
        if (!page) throw new SeitenFehler(`Quelle ${e.quelle} fehlt.`);
        return { page, drehung: e.drehung };
    });

    // Reihenfolge herstellen: alles entfernen (von hinten), dann in Zielfolge
    // wieder einsetzen. pdf-lib erlaubt das Wiedereinsetzen entfernter Seiten
    // desselben Dokuments — hängt sie dabei aber direkt unter die Wurzel.
    // Vorher darum festnageln, was die Seite bisher von Zwischenknoten erbte.
    for (const seite of hauptSeiten) nageleGeerbteAttributeFest(seite);
    for (let i = ziel.getPageCount() - 1; i >= 0; i--) ziel.removePage(i);
    for (const { page, drehung } of reihenfolge) {
        ziel.addPage(page);
        if (drehung) page.setRotation(degrees((page.getRotation().angle + drehung) % 360));
    }
    // Entfernte Seiten stehen sonst weiter in der Datei — samt Inhalt.
    const entfernt = new Set<PDFRef>(hauptSeiten.filter((_, i) => !gesehen.has(i)).map((p) => p.ref));
    raeumeAuf(ziel, entfernt);
    return ziel.save();
}

/**
 * Seitenattribute, die laut PDF-Spezifikation vererbt werden (MediaBox,
 * CropBox, Rotate, Resources), direkt an die Seite schreiben. Scanner und
 * Office-Exporte legen sie gern nur an einen Zwischenknoten des Seitenbaums
 * (Pages → Pages → Page); nach dem Umhängen fehlten sie sonst — falsches
 * Format, falsche Drehung, Text ohne Schrift.
 */
function nageleGeerbteAttributeFest(seite: PDFPage): void {
    const node = seite.node;
    for (const name of ["MediaBox", "CropBox", "Rotate", "Resources"]) {
        const key = PDFName.of(name);
        if (node.get(key) !== undefined) continue;
        const geerbt = node.getInheritableAttribute(key);
        if (geerbt !== undefined) node.set(key, geerbt);
    }
}

/** Bild vom PC als eigene A4-Seite (hoch/quer nach Bildformat), eingepasst mit 24 pt Rand. */
export async function bildAlsPdf(bytes: Uint8Array, mime: "image/png" | "image/jpeg"): Promise<Uint8Array> {
    const d = await PDFDocument.create();
    const bild = mime === "image/png" ? await d.embedPng(bytes) : await d.embedJpg(bytes);
    const quer = bild.width > bild.height;
    const [w, h] = quer ? [841.89, 595.28] : [595.28, 841.89];
    const rand = 24;
    // Auf die Seite einpassen, Seitenverhältnis bleibt.
    const s = Math.min((w - 2 * rand) / bild.width, (h - 2 * rand) / bild.height);
    const bw = bild.width * s;
    const bh = bild.height * s;
    d.addPage([w, h]).drawImage(bild, { x: (w - bw) / 2, y: (h - bh) / 2, width: bw, height: bh });
    return d.save();
}
